/**
 * Safely parses a URL string into a URL object, handling standard and encoded 
 * (including double-encoded) URLs gracefully.
 *
 * @param {string} href - The URL string to parse.
 * @returns {URL | Object} Returns a parsed URL object, or an empty object {} if parsing fails.
 */
export const getParsedUrl = (href) => {
  let currentHref = href;
  // Try to parse the URL, if it fails, try to decode it and parse again.
  // We do this up to 2 times to handle double encoding which sometimes occurs.
  for (let i = 0; i < 2; i++) {
    try {
      return new URL(currentHref);
    } catch (e) {
      try {
        const decoded = decodeURIComponent(currentHref);
        if (decoded === currentHref) break;
        currentHref = decoded;
      } catch (err) {
        break;
      }
    }
  }
  try {
    return new URL(currentHref);
  } catch (e) {
    return {};
  }
};

/**
 * Checks if a specific query parameter in a given URL evaluates to a truthy value ('1' or 'true').
 *
 * @param {string} name - The name of the query parameter to check.
 * @param {string} [href=window.location.href] - The URL to parse and check. Defaults to the current window location.
 * @returns {boolean} `true` if the parameter equals '1' or 'true', otherwise `false`.
 */
export const isParamTrue = (name, href = window.location.href) => {
  const url = getParsedUrl(href);
  const paramValue = url.searchParams?.get(name);
  return paramValue && !!paramValue.match(/^(1|true)$/);
};

/**
 * Checks if a specific query parameter in a given URL evaluates to a falsy value ('0' or 'false').
 *
 * @param {string} name - The name of the query parameter to check.
 * @param {string} [href=window.location.href] - The URL to parse and check. Defaults to the current window location.
 * @returns {boolean} `true` if the parameter equals '0' or 'false', otherwise `false`.
 */
export const isParamFalse = (name, href = window.location.href) => {
  const url = getParsedUrl(href);
  const paramValue = url.searchParams?.get(name);
  return paramValue && !!paramValue.match(/^(0|false)$/);
};

/**
 * Extracts all UTM tracking tags (parameters starting with 'utm_') from a given URL.
 *
 * @param {string} [href=window.location.href] - The URL to extract tags from. Defaults to the current window location.
 * @returns {{ hasUtmTags: boolean, utmTags: Array<{key: string, value: string}>, utmObject: Object.<string, string> }} An object containing a boolean flag indicating if UTM tags exist, an array of the extracted tags, and a key-value object of the tags.
 */
export const getUtmTags = (href = window.location.href) => {
  const url = getParsedUrl(href);
  if (!url.searchParams) return { hasUtmTags: false, utmTags: [], utmObject: {} };
  const searchParamArr = [...url.searchParams.entries()];
  
  const utmTags = [];
  const utmObject = {};

  searchParamArr.forEach(([key, value]) => {
    if (key.startsWith('utm_')) {
      utmTags.push({ key, value });
      utmObject[key] = value;
    }
  });

  const hasUtmTags = utmTags.length > 0;
  return {
    hasUtmTags,
    utmTags,
    utmObject,
  };
};
